import { CircularProgress, Modal, Typography } from "@mui/material";
import { parseEther, parseUnits } from "viem";
import moment from "moment";
import { useEffect, useState } from "react";
import { erc20ABI, useAccount, useBalance, useWalletClient } from "wagmi";
import { getContract, waitForTransaction } from "wagmi/actions";
import calculateAPR from "../../helpers/calculateAPR";
import {
  ASSETS_TO_BORROW,
  CREATE_BORROW_EIP712_TYPES,
  CREATE_DYNAMIC_BORROW_EIP712_DOMAIN,
  LENDING_CONTRACT_ADDRESS,
  OrderKind,
  TokenKind,
} from "../../lib/constants";
import createLendOffer from "../../services/createLendOffer";
import fetchCollectionItem from "../../services/fetchCollectionItem";
import { AssetToBorrowDropdown } from "../dropdown/asset_to_borrow_dropdown";
import { BorrowDurationDropdown } from "../dropdown/borrow_duration_dropdown";
import { CollectionsDropdown } from "../dropdown/collections_dropdown";
import { ExpireDaysDropdown } from "../dropdown/expire_days_dropdown";
import { errorToast, successToast } from "../toast";
import { wrappedTokenABI } from "../../helpers/wrappedTokenABI";
import { handleConvert } from "../../helpers/handleConvert";
import { calcPercBigNumber } from "../../helpers/calculatePerc";
import { useConnectModal } from "@rainbow-me/rainbowkit";

export const GlobalOfferModal = ({
  globalOfferModalOpen,
  setGlobalOfferModalOpen,
  collections,
  selectedCollectionInModal,
  setSelectedCollectionInModal,
}: {
  globalOfferModalOpen: any;
  setGlobalOfferModalOpen: any;
  collections: any;
  selectedCollectionInModal: any;
  setSelectedCollectionInModal: any;
}) => {
  const { openConnectModal } = useConnectModal();
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const { address, isConnected } = useAccount();
  const [assetToBorrow, setAssetToBorrow] = useState<IAssetToBorrow>(
    ASSETS_TO_BORROW[0]
  );
  const [assetAmount, setAssetAmount] = useState("1");
  const [expireDays, setExpireDays] = useState(1);
  const [borrowDuration, setBorrowDuration] = useState(7);
  const [interestRate, setInterestRate] = useState("");

  const [amount, setAmount] = useState("1");

  const { data: walletClient, status } = useWalletClient();

  const [isApprovedERC20, setIsApprovedERC20] = useState(false);

  const [apr, setApr] = useState(0);

  const [data, setData] = useState<any>();

  const [tabsActive, setTabsActive] = useState(2);

  let { data: nativeBalance } = useBalance({ address, watch: true });
  let { data: wrappedTokenBalance } = useBalance({
    address,
    token: assetToBorrow?.address,
    watch: true,
  });
  const totalBalance =
    parseFloat(nativeBalance?.formatted || "0") +
    parseFloat(wrappedTokenBalance?.formatted || "0");
  const isTotalBalanceEnough = totalBalance >= parseFloat(assetAmount);

  const tokenContract = getContract({
    address: assetToBorrow?.address,
    abi: wrappedTokenABI,
    walletClient,
  });

  const handleAmountChanged = (newAmount) => {
    newAmount = newAmount.replace(",", ".");
    const zeroCounts = newAmount.split(".");

    if (zeroCounts?.length >= 1 && zeroCounts[1]?.length > 4) return;

    setAssetAmount(newAmount);
  };

  const approveForERC20 = async () => {
    if (status !== "success") {
      return;
    }

    setButtonLoading(true);
    try {
      const selectedCurrencyContract = getContract({
        address: assetToBorrow.address,
        abi: erc20ABI,
        walletClient,
      });

      let approve_amount = BigInt(
        "115792089237316195423570985008687907853269984665640564039457584007913129639935"
      ); //(2^256 - 1 )

      const tx = await selectedCurrencyContract.write.approve(
        [LENDING_CONTRACT_ADDRESS, approve_amount],
        {}
      );

      await waitForTransaction({ hash: tx });

      let currencyAllowance: bigint =
        await selectedCurrencyContract.read.allowance([
          address,
          LENDING_CONTRACT_ADDRESS,
        ]);
      // console.log(currencyAllowance.toString());
      setIsApprovedERC20(currencyAllowance > parseEther(assetAmount));
    } catch (error) {
      console.log(error);
    }
    setButtonLoading(false);
  };

  const fetchData = async () => {
    if (!selectedCollectionInModal) {
      return;
    }

    const res = await fetchCollectionItem({
      collectionAddress:
        selectedCollectionInModal &&
        selectedCollectionInModal.address.toString(),
      itemId: 1,
    });
    setData(res.data);
    setLoading(false);
  };

  const submitOffer = async () => {
    if (status !== "success") {
      return;
    }

    if (!isTotalBalanceEnough) return errorToast("Insufficient Balance!");
    if (!assetToBorrow) return errorToast("Select The Currency!");
    if (!assetAmount) return errorToast("Set The Amount!");
    if (!expireDays)
      return errorToast("Set How Many Days You Want It To Expire!");
    if (!borrowDuration) return errorToast("Set Duration!");
    if (!interestRate || Number(interestRate) === 0)
      return errorToast("Set Interest Rate!");

    setButtonLoading(true);
    try {
      await handleConvert({
        amount: assetAmount,
        balance: wrappedTokenBalance,
        contract: tokenContract,
      });
      if (!isApprovedERC20) {
        await approveForERC20();
      }

      const signingTime = moment().unix();
      const duration = moment.duration(borrowDuration, "days").asSeconds();
      const expiration = moment.duration(expireDays, "days").asSeconds();
      const nftAddresses = [
        data.collectionAddress || data?.collection?.address,
      ];
      const colleteralTokenIDs = [Number(data.itemId)];
      const orderKind = OrderKind.OFFER;
      const tokenKinds = [
        selectedCollectionInModal?.contractType === "ERC721"
          ? TokenKind.ERC721
          : TokenKind.ERC1155,
      ];
      const borrowAmount = parseUnits(assetAmount, 18).toString();
      const paymentToken = assetToBorrow.address;
      const paymentTokenName = assetToBorrow.name;
      const amounts =
        tabsActive === 2
          ? [Number(amount)]
          : [Number(data?.itemowner[0]?.amount) || 1];
      const _interestRate = Number(interestRate) * 100;

      let inputForSignature = {
        issuer: address,
        nftAddresses: nftAddresses,
        colleteralTokenIDs: colleteralTokenIDs,
        amounts: amounts,
        paymentToken: paymentToken,
        borrowAmount: borrowAmount,
        interestRate: _interestRate,
        times: {
          signingTime: signingTime,
          expiration: expiration,
          duration: duration,
        },
        orderKind: orderKind,
        tokenKinds: tokenKinds,
        isGlobal: tabsActive === 2,
      };

      const typedData = JSON.stringify({
        domain: CREATE_DYNAMIC_BORROW_EIP712_DOMAIN(data?.chain),
        types: CREATE_BORROW_EIP712_TYPES,
        message: inputForSignature,
        primaryType: "Order",
      });

      const signature = await (window?.ethereum?.request as any)({
        method: "eth_signTypedData_v4",
        params: [address, typedData],
      });

      let input: any = {
        issuer: address,
        amount: amounts[0].toString(),
        borrowAmount: borrowAmount,
        colleteralTokenID: colleteralTokenIDs[0].toString(),
        duration: duration.toString(),
        expiration: expiration.toString(),
        signingTime: signingTime.toString(),
        interestRate: _interestRate,
        isGlobal: tabsActive === 2,
        nftAddress: nftAddresses[0],
        orderKind: orderKind,
        tokenKind: tokenKinds[0],
        paymentToken: paymentToken,
        paymentTokenName: paymentTokenName,
        // lendOrdersId: lendData.id,
      };

      console.log(signature);

      input.signature = signature;

      const res = await createLendOffer(input);

      successToast("Offer Created Successfully!");
      setGlobalOfferModalOpen(false);
    } catch (err) {
      console.log(err);
      errorToast(
        err?.reason ? err?.reason : err?.message ? err?.message : "err:" + err
      );
    }
    setButtonLoading(false);
  };
  // console.log("selectedCollectionInModal", selectedCollectionInModal);

  useEffect(() => {
    if (globalOfferModalOpen) {
      fetchData();
    }

    return () => {};
  }, [selectedCollectionInModal, globalOfferModalOpen]);

  useEffect(() => {
    (async () => {
      if (!assetToBorrow || !assetAmount) {
        return;
      }

      const selectedCurrencyContract = getContract({
        address: assetToBorrow.address,
        abi: erc20ABI,
        walletClient,
      });

      let currencyAllowance: bigint =
        await selectedCurrencyContract.read.allowance([
          address,
          LENDING_CONTRACT_ADDRESS,
        ]);
      setIsApprovedERC20(currencyAllowance > parseEther(assetAmount));
    })();

    return () => {};
  }, [assetToBorrow, assetAmount]);

  useEffect(() => {
    assetAmount &&
      borrowDuration &&
      interestRate &&
      setApr(Number(calculateAPR(borrowDuration, interestRate)));

    return () => {};
  }, [assetAmount, borrowDuration, interestRate]);

  const paybackAmount = Number(
    Number(assetAmount) + (Number(assetAmount) / 100) * Number(interestRate)
  ).toFixed(4);

  const paybackAmountWithFee = calcPercBigNumber(parseEther(paybackAmount), 99);

  const buttonDisabled =
    !isConnected ||
    buttonLoading ||
    apr > 500 ||
    !isTotalBalanceEnough ||
    !parseFloat(assetAmount) ||
    parseFloat(paybackAmountWithFee) < parseFloat(assetAmount);

  return (
    <Modal
      open={globalOfferModalOpen}
      onClose={() => setGlobalOfferModalOpen(false)}
      sx={{
        display: "flex",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div className="p-4 w-full max-w-xl">
        <div className="dark:bg-jacarta-700 dark:border-jacarta-600 border-jacarta-100 rounded-2lg border bg-white p-6 max-h-[95vh] overflow-scroll">
          <div className="flex justify-between items-center relative">
            <h2 className="font-display text-jacarta-700 py-4 text-2xl font-medium dark:text-white">
              Global Offer
            </h2>
            <button
              onClick={() => setGlobalOfferModalOpen(false)}
              className="btn-close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                className="fill-jacarta-700 h-6 w-6 dark:fill-white"
              >
                <path fill="none" d="M0 0h24v24H0z"></path>
                <path d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"></path>
              </svg>
            </button>
          </div>
          <h4 className="font-display text-jacarta-700 pt-4 mb-2 text-lg font-medium dark:text-white">
            Select Collection
          </h4>
          <div className="flex flex-col cursor-pointer mb-6">
            <CollectionsDropdown
              collections={collections}
              selectedCollection={selectedCollectionInModal}
              setSelectedCollection={setSelectedCollectionInModal}
            />
          </div>

          <div className="dark:bg-jacarta-700  rounded-2lg  bg-white">
            <div className="sm:flex sm:flex-wrap">
              {loading ? (
                <div className="flex justify-center items-center w-full">
                  {" "}
                  <CircularProgress />{" "}
                </div>
              ) : (
                <div className="sm:grid grid-cols-2 gap-4 w-full items-center sm:space-y-0 space-y-4">
                  <div className="relative">
                    <div>
                      <label className="font-display text-jacarta-700 mb-2 block dark:text-white">
                        Currency
                      </label>
                    </div>

                    {/* dropdown */}
                    <div className="dropdown my-1 cursor-pointer">
                      <AssetToBorrowDropdown
                        assetToBorrow={assetToBorrow}
                        setAssetToBorrow={setAssetToBorrow}
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="item-external-link"
                      className="font-display text-jacarta-700 mb-2 block dark:text-white"
                    >
                      Amount
                    </label>
                    <div className="flex items-center relative">
                      <input
                        type="number"
                        className="dark:bg-jacarta-700 border-jacarta-100 hover:ring-accent/10 focus:ring-accent dark:border-jacarta-600 dark:placeholder:text-jacarta-300 w-full rounded-lg py-3 px-3 hover:ring-2 dark:text-white"
                        placeholder="Amount"
                        min={0}
                        value={assetAmount}
                        // onChange={(e) => setAssetAmount(e.target.value)}
                        onChange={(e) =>
                          String(e.target.value).length < 9
                            ? handleAmountChanged(e.target.value)
                            : () => {}
                        }
                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      />
                      {assetToBorrow && (
                        <span
                          style={{ right: "15%" }}
                          className="absolute dark:text-jacarta-300 text-black"
                        >
                          {assetToBorrow.name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="item-external-link"
                      className="font-display text-jacarta-700 mb-2 block dark:text-white"
                    >
                      Expire After
                    </label>
                    <ExpireDaysDropdown
                      expireDays={expireDays}
                      setExpireDays={setExpireDays}
                    />
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="item-external-link"
                      className="font-display text-jacarta-700 mb-2 block dark:text-white"
                    >
                      Duration
                    </label>
                    {/* <p className="dark:text-jacarta-300 text-2xs mb-3">
                                          For how many days you will borrow.
                                      </p> */}
                    {/* <div className="flex items-center relative">
                          <input
                            type="number"
                            className="dark:bg-jacarta-700 border-jacarta-100 hover:ring-accent/10 focus:ring-accent dark:border-jacarta-600 dark:placeholder:text-jacarta-300 w-full rounded-lg py-3 px-3 hover:ring-2 dark:text-white"
                            placeholder="Specify Days"
                            min={1}
                            value={borrowDuration}
                            onChange={(e) =>
                              String(e.target.value).length < 6
                                ? setBorrowDuration(e.target.value)
                                : () => { }
                            }
                          />
                          {borrowDuration !== "" && (
                            <span
                              style={{ right: "15%" }}
                              className="absolute dark:text-jacarta-300 text-black"
                            >
                              Days
                            </span>
                          )}
                        </div>
                        */}
                    <BorrowDurationDropdown
                      borrowDuration={borrowDuration}
                      setBorrowDuration={setBorrowDuration}
                    />
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="item-external-link"
                      className="font-display text-jacarta-700 mb-2 block dark:text-white"
                    >
                      Interest Rate
                    </label>
                    {/* <p className="dark:text-jacarta-300 text-2xs mb-3">
                                          For how many days you will borrow.
                                      </p> */}
                    <div className="flex items-center relative">
                      <input
                        type="number"
                        className="dark:bg-jacarta-700 border-jacarta-100 hover:ring-accent/10 focus:ring-accent dark:border-jacarta-600 dark:placeholder:text-jacarta-300 w-full rounded-lg py-3 px-3 hover:ring-2 dark:text-white"
                        placeholder="Specify Interest Rate"
                        min={1}
                        value={interestRate}
                        onChange={(e) =>
                          String(e.target.value).length < 6
                            ? setInterestRate(e.target.value)
                            : () => {}
                        }
                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      />
                      {interestRate !== "" && (
                        <span
                          style={{ right: "15%" }}
                          className="absolute dark:text-jacarta-300 text-black"
                        >
                          %
                        </span>
                      )}
                    </div>
                  </div>

                  {tabsActive === 2 && (
                    <div className="relative">
                      <label
                        htmlFor="item-external-link"
                        className="font-display text-jacarta-700 mb-2 block dark:text-white"
                      >
                        Global Offer Quantity
                      </label>
                      <div className="flex items-center relative">
                        <input
                          type="number"
                          disabled
                          className="dark:bg-jacarta-900 bg-jacarta-100 border-jacarta-100 hover:ring-accent/10 focus:ring-accent dark:border-jacarta-600 dark:placeholder:text-jacarta-300 w-full rounded-lg py-3 px-3 hover:ring-2 dark:text-white"
                          placeholder="Amount"
                          min={0}
                          defaultValue={1}
                          // value={amount}
                          value={"1"}
                          // onChange={(e) =>
                          //   String(e.target.value).length < 9
                          //     ? handleGlobalAmountChanged(e.target.value)
                          //     : () => {}
                          // }
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        />
                      </div>
                    </div>
                  )}

                  <div className="relative  h-full  ">
                    <label
                      htmlFor="item-external-link"
                      className="font-display text-jacarta-700 block dark:text-white"
                    >
                      Payback Amount
                    </label>

                    <Typography className="mt-5 text-black dark:text-jacarta-200 ">
                      {paybackAmount} {assetToBorrow?.name}{" "}
                      {assetAmount &&
                        borrowDuration &&
                        interestRate &&
                        `(APR: ${calculateAPR(borrowDuration, interestRate)}%)`}
                    </Typography>
                    {Number(apr) > 500 && (
                      <Typography className="mt-6 text-black dark:text-jacarta-200 !normal-case">
                        <span className="!text-[#ef5353]">Warning: </span> APR
                        is too high, please lower your input.
                      </Typography>
                    )}
                  </div>

                  <div className="relative alert-info w-fit col-span-full p-2 text-2xs rounded-lg leading-4 !normal-case">
                    There is a 1% service fee on the amount you’ll be paid back.
                    {assetAmount &&
                      borrowDuration &&
                      interestRate &&
                      Number(assetAmount) > 0 && (
                        <span className="!normal-case">
                          {" "}
                          You will receive {paybackAmountWithFee}{" "}
                          {assetToBorrow?.name}
                        </span>
                      )}
                  </div>

                  {parseFloat(paybackAmountWithFee) < parseFloat(assetAmount) &&
                    parseFloat(assetAmount) > 0 &&
                    interestRate && (
                      <div className="relative alert-info w-fit col-span-full p-2 text-2xs rounded-lg leading-4 !normal-case">
                        <span className="!text-[#ef5353]">Warning: </span> After
                        the service the amount paid back will be less than the
                        amount you lent. Please change the Interest Rate.
                      </div>
                    )}

                  {!isConnected ? (
                    <button
                      className={
                        "bg-accent col-span-2 mt-5 w-full shadow-accent-volume hover:bg-accent-dark inline-block  rounded-full py-3 px-8 text-center font-semibold text-white transition-all"
                      }
                      onClick={openConnectModal}
                    >
                      Please Connect Your Wallet
                    </button>
                  ) : (
                    <button
                      disabled={buttonDisabled}
                      onClick={submitOffer}
                      className={
                        buttonDisabled
                          ? "bg-[#909090] col-span-2 mt-5 w-full border border-jacarta-100 inline-block  rounded-full py-3 px-8 text-center font-semibold text-white transition-all"
                          : isConnected
                          ? "bg-accent col-span-2 mt-5 w-full shadow-accent-volume hover:bg-accent-dark inline-block  rounded-full py-3 px-8 text-center font-semibold text-white transition-all"
                          : "bg-white col-span-2 mt-5 w-full border border-jacarta-100 inline-block  rounded-full py-3 px-8 text-center font-semibold text-black transition-all"
                      }
                    >
                      {buttonLoading ? (
                        <CircularProgress size={24} />
                      ) : apr > 500 ? (
                        "Apr is Too High (Max: 500)"
                      ) : !isTotalBalanceEnough || !parseFloat(assetAmount) ? (
                        "Insufficient Balance"
                      ) : (
                        "Make Offer"
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
